/**
 * Builds the CSS animation for the Pimo logo loader.
 *
 * Every variant only describes how the four logo shapes move (position, scale, depth) over one
 * cycle. This module turns that into keyframes, sorts the shapes into depth layers and knocks a
 * GAP-wide ring out of everything behind a shape, which is what draws the logo's cut-outs.
 */

export type ShapeName = "orange" | "green" | "yellow" | "cyan";

export interface LogoShape {
  name: ShapeName;
  color: string;
  /** bounding box in logo coordinates: x0, y0, x1, y1 */
  box: [number, number, number, number];
  /** the shape as an uncut rounded square; cut-outs come from the knockout masks */
  path: string;
}

export const LOGO_SHAPES: LogoShape[] = [
  {
    name: "orange",
    color: "#FF5E1E",
    box: [19.3315, 0, 42.0599, 22.7286],
    path: "M25.0137 0H36.3779C39.5139 0 42.0599 2.5456 42.0599 5.68214V17.0464C42.0599 20.182 39.5139 22.7286 36.3779 22.7286H25.0137C21.8776 22.7286 19.3315 20.182 19.3315 17.0464V5.68214C19.3315 2.5456 21.8776 0 25.0137 0Z",
  },
  {
    name: "green",
    color: "#00B46F",
    box: [19.2885, 32.4803, 47.8093, 61.0014],
    path: "M47.8093 39.0402V54.4415C47.8093 58.0618 44.8699 61.0014 41.2495 61.0014H25.8483C22.2279 61.0014 19.2885 58.0618 19.2885 54.4415V39.0402C19.2885 35.4199 22.2279 32.4803 25.8483 32.4803H41.2495C44.8699 32.4803 47.8093 35.4199 47.8093 39.0402Z",
  },
  {
    name: "yellow",
    color: "#FFCF1E",
    box: [31.3149, 12.3206, 67.1721, 48.1781],
    path: "M40.2793 12.3206H58.2079C63.1555 12.3206 67.1721 16.3366 67.1721 21.2849V39.2137C67.1721 44.1605 63.1555 48.1781 58.2079 48.1781H40.2793C35.3317 48.1781 31.3149 44.1605 31.3149 39.2137V21.2849C31.3149 15.9031 34.898 12.3206 40.2793 12.3206Z",
  },
  {
    name: "cyan",
    color: "#00D6DD",
    box: [0, 20, 17, 37.0002],
    path: "M17 24.25V32.7501C17 35.0954 15.0957 37.0002 12.75 37.0002H4.25004C1.90439 37.0002 0 35.0954 0 32.7501V24.25C0 21.904 1.90439 20 4.25004 20H12.75C15.0957 20 17 21.904 17 24.25Z",
  },
];

/** width of the knockout ring around every shape (the logo's own spacing) */
export const GAP = 2.2;

/** shared by all variants so the logo keeps its size when the variant changes */
export const VIEW_BOX = { x: -21, y: -15, size: 95 };

const shapeByName = Object.fromEntries(
  LOGO_SHAPES.map((shape) => [shape.name, shape])
) as Record<ShapeName, LogoShape>;

export const SHAPE_NAMES = LOGO_SHAPES.map((shape) => shape.name);

export function shapeCenter(name: ShapeName): [number, number] {
  const [x0, y0, x1, y1] = shapeByName[name].box;
  return [(x0 + x1) / 2, (y0 + y1) / 2];
}

export function shapeSize(name: ShapeName): number {
  const [x0, y0, x1, y1] = shapeByName[name].box;
  return Math.max(x1 - x0, y1 - y0);
}

/*
 * The logo already reads as a ring seen from above: green front, yellow right, orange back,
 * cyan left. The ellipse (vertical axis at RING.x) is fitted to those four shapes.
 */
const [cyanX] = shapeCenter("cyan");
const [yellowX] = shapeCenter("yellow");
const [, orangeY] = shapeCenter("orange");
const [, greenY] = shapeCenter("green");

export const RING = {
  x: (cyanX + yellowX) / 2,
  y: (orangeY + greenY) / 2,
  rx: (yellowX - cyanX) / 2,
  ry: (greenY - orangeY) / 2,
};

export function ringPoint(angle: number, spread = 1): [number, number] {
  return [
    RING.x + spread * RING.rx * Math.sin(angle),
    RING.y + spread * RING.ry * Math.cos(angle),
  ];
}

/** where each shape sits on the ring in the logo (0 = front, clockwise seen from above) */
export const RING_PHASE = Object.fromEntries(
  SHAPE_NAMES.map((name) => {
    const [cx, cy] = shapeCenter(name);
    return [name, Math.atan2((cx - RING.x) / RING.rx, (cy - RING.y) / RING.ry)];
  })
) as Record<ShapeName, number>;

/** depth of each shape in the logo; higher is closer. Gives orange < cyan < yellow < green. */
export const REST_DEPTH = Object.fromEntries(
  SHAPE_NAMES.map((name) => [name, Math.cos(RING_PHASE[name])])
) as Record<ShapeName, number>;

export interface Pose {
  /** centre of the shape, in logo coordinates */
  x: number;
  y: number;
  /** scale relative to the shape's size in the logo */
  s: number;
  /** depth; shapes with a higher z are drawn in front and cut into the ones behind */
  z: number;
}

export interface LoaderMotion {
  /** seconds per cycle */
  duration: number;
  /** pose of a shape at cycle fraction p (0..1); p = 0 and p = 1 must match for a seamless loop */
  pose: (name: ShapeName, p: number) => Pose;
}

export const restPose = (name: ShapeName): Pose => {
  const [x, y] = shapeCenter(name);
  return { x, y, s: 1, z: REST_DEPTH[name] };
};

const POSE_SAMPLES = 360;
const DEPTH_SAMPLES = 1200;
/** largest allowed deviation (logo units) when dropping keyframes */
const TOLERANCE = 0.05;

function num(value: number, digits = 2): string {
  const text = value.toFixed(digits).replace(/\.?0+$/, "");
  return text === "-0" ? "0" : text;
}

/** Ramer–Douglas–Peucker over the sampled poses: keeps only keyframes that change the path. */
function simplify(points: number[][]): number[] {
  const keep = new Set([0, points.length - 1]);
  const stack: [number, number][] = [[0, points.length - 1]];

  while (stack.length) {
    const [from, to] = stack.pop()!;
    let worst = -1;
    let worstError = TOLERANCE;

    for (let i = from + 1; i < to; i++) {
      const t = (i - from) / (to - from);
      const error = Math.hypot(
        ...points[i].map(
          (value, d) =>
            value - (points[from][d] + t * (points[to][d] - points[from][d]))
        )
      );
      if (error > worstError) {
        worst = i;
        worstError = error;
      }
    }

    if (worst > 0) {
      keep.add(worst);
      stack.push([from, worst], [worst, to]);
    }
  }

  return [...keep].sort((a, b) => a - b);
}

const restOrder = [...SHAPE_NAMES].sort(
  (a, b) => REST_DEPTH[a] - REST_DEPTH[b]
);

/** shapes sorted back to front at cycle fraction p */
export function depthOrder(motion: LoaderMotion, p: number): ShapeName[] {
  // stable sort: ties keep the logo's own order
  return [...restOrder].sort(
    (a, b) => motion.pose(a, p).z - motion.pose(b, p).z
  );
}

export function className(key: string, name: ShapeName, layer: number) {
  return `pimo-loader-${key}-${name}-${layer}`;
}

/**
 * CSS for one variant. Layer k shows, at any moment, the shape that is k-th from the back;
 * each shape therefore gets one transform animation plus one visibility animation per layer.
 */
export function buildLoaderCss(key: string, motion: LoaderMotion): string {
  const blocks: string[] = [];

  for (const name of SHAPE_NAMES) {
    const half = shapeSize(name) / 2;
    const times = Array.from(
      { length: POSE_SAMPLES + 1 },
      (_, i) => i / POSE_SAMPLES
    );
    const poses = times.map((p) => motion.pose(name, p));
    const kept = simplify(poses.map(({ x, y, s }) => [x, y, s * half]));

    const frames = kept.map((i) => {
      const { x, y, s } = poses[i];
      return `${num(times[i] * 100)}%{transform:translate(${num(x)}px,${num(y)}px) scale(${num(s, 3)})}`;
    });
    blocks.push(`@keyframes pimo-loader-${key}-${name}{${frames.join("")}}`);
  }

  const grid = Array.from(
    { length: DEPTH_SAMPLES },
    (_, i) => i / DEPTH_SAMPLES
  );
  const orders = grid.map((p) => depthOrder(motion, p));

  for (const name of SHAPE_NAMES) {
    for (let layer = 0; layer < SHAPE_NAMES.length; layer++) {
      const visible = orders.map((order) => order[layer] === name);
      const stops: string[] = [];
      let start = 0;

      for (let i = 1; i <= visible.length; i++) {
        if (i === visible.length || visible[i] !== visible[start]) {
          const from = grid[start] * 100;
          const to = i === visible.length ? 100 : grid[i] * 100 - 0.01;
          stops.push(
            `${num(from)}%,${num(to)}%{opacity:${visible[start] ? 1 : 0}}`
          );
          start = i;
        }
      }
      blocks.push(
        `@keyframes ${className(key, name, layer)}{${stops.join("")}}`
      );
    }
  }

  const rules = (duration: number) =>
    SHAPE_NAMES.flatMap((name) =>
      SHAPE_NAMES.map((_, layer) => {
        const cls = className(key, name, layer);
        return `.${cls}{animation:pimo-loader-${key}-${name} ${duration}s linear infinite,${cls} ${duration}s linear infinite}`;
      })
    ).join("");

  blocks.push(rules(motion.duration));
  blocks.push(
    `@media (prefers-reduced-motion:reduce){${rules(motion.duration * 2.5)}}`
  );

  return blocks.join("\n");
}
